import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, FileText, Link2, Loader2, SlidersHorizontal, Sparkles, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTeamMembers } from "@/hooks/teams/useTeamMembers";
import { ConversationService } from "@/services/conversation/ConversationService";
import type { Conversation } from "@/types/conversation/conversation";
import type { ListenedResource } from "@/types/conversation/listened";

/**
 * The pieces a listened email's reply card and a listened call's follow-up
 * card share: who is taking it, handled state, what to teach the genie, and
 * the genie's own resources that help.
 */

const UNASSIGNED = "unassigned";

export function ListenedAssignee({ conversation }: { conversation: Conversation }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: members = [] } = useTeamMembers();
  const [assignedTo, setAssignedTo] = useState<string | null>(conversation.assigned_to ?? null);
  const [saving, setSaving] = useState(false);

  const teammates = members.filter((member) => member.role !== "Invited");
  if (teammates.length < 2) return null;

  const nameOf = (id: string) => {
    const member = teammates.find((m) => m.id === id);
    return member?.full_name || member?.email || "A teammate";
  };

  const handleChange = async (value: string) => {
    const next = value === UNASSIGNED ? null : value;
    const previous = assignedTo;
    setAssignedTo(next);
    setSaving(true);
    try {
      await ConversationService.assign(conversation.id, next);
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      toast.success(
        next ? `${next === user?.id ? "You're" : `${nameOf(next)} is`} on it. Reminders go to them only.` : "Unassigned",
      );
    } catch (error) {
      setAssignedTo(previous);
      toast.error(error instanceof Error ? error.message : "Couldn't assign it. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2.5">
      <p className="inline-flex items-center gap-1.5 text-sm text-gray-700">
        <UserRound className="w-4 h-4 text-genie-primary" aria-hidden />
        {!assignedTo
          ? "Nobody's taking this yet"
          : assignedTo === user?.id
            ? "You're taking this"
            : `${nameOf(assignedTo)} is taking this`}
      </p>
      <div className="flex items-center gap-2">
        {assignedTo !== user?.id && user && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleChange(user.id)}
            disabled={saving}
            className="min-h-[44px] sm:min-h-0 shrink-0"
          >
            Take it
          </Button>
        )}
        <Select value={assignedTo ?? UNASSIGNED} onValueChange={handleChange} disabled={saving}>
          <SelectTrigger className="min-h-[44px] sm:min-h-0 sm:h-9 w-full sm:w-44 bg-white text-base sm:text-sm" aria-label="Assign to">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {teammates.map((member) => (
              <SelectItem key={member.id} value={member.id}>
                {member.id === user?.id ? "Me" : member.full_name || member.email}
              </SelectItem>
            ))}
            {assignedTo && !teammates.some((m) => m.id === assignedTo) && (
              <SelectItem value={assignedTo}>A teammate</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

interface ListenedHandledBarProps {
  handled: boolean;
  saving: boolean;
  onToggle: () => void;
  /** Shown while open: how to tell Help Genie it was answered elsewhere. */
  openMessage: string;
  /** Shown once handled. */
  handledMessage: string;
  /** Highlights the bar after the team opened a reply outside Help Genie. */
  nudged?: boolean;
}

export function ListenedHandledBar({ handled, saving, onToggle, openMessage, handledMessage, nudged }: ListenedHandledBarProps) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg px-3 py-2.5 ${
        handled ? "bg-genie-primary/5" : nudged ? "bg-genie-accent/10" : "bg-gray-50"
      }`}
    >
      <p className="text-sm text-gray-700">
        {handled ? (
          <span className="inline-flex items-center gap-1.5 font-medium text-genie-primary">
            <CheckCircle2 className="w-4 h-4" />
            {handledMessage}
          </span>
        ) : (
          openMessage
        )}
      </p>
      <Button
        type="button"
        variant={handled ? "ghost" : "outline"}
        size="sm"
        onClick={onToggle}
        disabled={saving}
        className="min-h-[44px] sm:min-h-0 shrink-0"
      >
        {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
        {handled ? "Reopen" : "Mark handled"}
      </Button>
    </div>
  );
}

interface TeachGenieNoteProps {
  gapNote: string;
  agentId: string | null | undefined;
  /** "emails" or "calls": what the genie should learn to handle. */
  subject: string;
}

export function TeachGenieNote({ gapNote, agentId, subject }: TeachGenieNoteProps) {
  return (
    <div className="rounded-lg bg-genie-accent/10 px-3 py-3 space-y-2">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-genie-accent" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-gray-900">Teach your genie to answer this</p>
          <p className="text-sm text-gray-700 mt-0.5">{gapNote}</p>
          <p className="text-xs text-gray-500 mt-1">
            Add the missing facts to its knowledge base, or tell it how to handle {subject} like this in its
            instructions. Its next suggestions will need less editing.
          </p>
        </div>
      </div>
      {agentId && (
        <div className="flex flex-col sm:flex-row gap-2 sm:pl-6">
          <Button type="button" variant="outline" size="sm" asChild className="min-h-[44px] sm:min-h-0">
            <Link to={`/genie-hub/knowledge-base/${agentId}`}>
              <BookOpen className="w-4 h-4 mr-1.5" />
              Add to knowledge base
            </Link>
          </Button>
          <Button type="button" variant="outline" size="sm" asChild className="min-h-[44px] sm:min-h-0">
            <Link to={`/genie-hub/my-genies/${agentId}?section=section-prompts`}>
              <SlidersHorizontal className="w-4 h-4 mr-1.5" />
              Edit genie instructions
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}

export function ListenedResources({ resources }: { resources: ListenedResource[] }) {
  if (resources.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-gray-900">Resources that may help</p>
      <ul className="space-y-1.5">
        {resources.map((resource) => (
          <li key={resource.url}>
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-[44px] items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2 hover:border-genie-primary transition-colors"
            >
              {resource.kind === "file" ? (
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-genie-primary" />
              ) : (
                <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-genie-primary" />
              )}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 truncate">{resource.title}</span>
                {resource.description && (
                  <span className="block text-xs text-gray-500 line-clamp-2">{resource.description}</span>
                )}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
